import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import { goBack } from 'connected-react-router';
import { initiateTokenRefresh, toggleRefreshSpinner, TokenRefreshLocalKey, TokenRefreshSessionKeys } from 'Core/ducks/refreshToken';
import { AsyncResult, fromResult, Loaded, Loading, ServerError } from 'Core/models/Async';
import { ValidEmail } from 'Core/models/Email';
import { NonEmptyString } from 'Core/models/EmptyString';
import { ValidPhoneNumber } from 'Core/models/PhoneNumber';
import { setRefreshReRouteOptions } from 'Core/refreshTokenUtilityMethods';
import { isLeft, isRight, left, map as mapEither, right } from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { ReducerState } from 'rootReducer';
import { mergeWithNextPage } from 'Shared/utilities/PageUtility';
import { prop } from 'Shared/utilities/prop';
import { getApiCallGenerator, PageFetchAction } from 'Shared/utilities/sagaApiGenerators';
import agentApi from 'Users/api/agentApi';
import userApi from 'Users/api/userApi';
import {
  AgentAdministrationActionType,
  changeAgentMapping,
  changeAgents,
  clearEmulation,
  createAgentMapping,
  fetchAgents,
  getAgent,
  impersonateAgent,
  loadAgentMapping,
  offboardAgent,
  setEmail,
  setPhone,
  setUsername,
  stopImpersonation,
  validateEmail,
  validatePhone,
  validateUsername,
} from 'Users/ducks/agentAdministration';
import { AgentMapping, ErrorSavingAgentMapping, toAgentMappingValue } from 'Users/models/AgentMapping';
import { UserModel } from 'Users/models/UserModel';

export function* validateUsernameApi(action: ReturnType<typeof validateUsername>) {
  yield put(setUsername(new Loading(left(NonEmptyString.create(action.payload.username)))));

  const result: AsyncResult<any> = yield call(userApi.validateAllstateUsername, action.payload.username);

  const isUsernameTaken = isLeft(result);

  const mapUsername = (username: string) =>
    isUsernameTaken ? left(NonEmptyString.create(username)) : right(NonEmptyString.create(username) as NonEmptyString);

  const usernameValue = new Loaded(mapUsername(action.payload.username));

  yield put(setUsername(usernameValue));
}

export function* validateUsernameSaga() {
  yield takeLatest(AgentAdministrationActionType.VALIDATE_USERNAME, validateUsernameApi);
}

export function* validatePhoneNumberApi(action: ReturnType<typeof validatePhone>) {
  const validatedPhone = ValidPhoneNumber.create(action.payload.phone);

  if (!(validatedPhone instanceof ValidPhoneNumber)) {
    yield put(setPhone(new Loaded(left(validatedPhone))));
  } else {
    yield put(setPhone(new Loading(left(ValidPhoneNumber.create(action.payload.phone)))));

    const result: AsyncResult<any> = yield call(userApi.validatePhoneNumber, action.payload.phone);

    const isPhoneNumberTaken = isLeft(result);

    const mapPhoneNumber = (phoneNumber: string) =>
      isPhoneNumberTaken
        ? left(ValidPhoneNumber.create(phoneNumber))
        : right(ValidPhoneNumber.create(phoneNumber) as ValidPhoneNumber);

    const phoneValue = new Loaded(mapPhoneNumber(action.payload.phone));

    yield put(setPhone(phoneValue));
  }
}

export function* validatePhoneNumberSaga() {
  yield takeLatest(AgentAdministrationActionType.VALIDATE_PHONE, validatePhoneNumberApi);
}
export function* validateEmailApi(action: ReturnType<typeof validateEmail>) {
  const validEmail = ValidEmail.create(action.payload.email);

  if (!(validEmail instanceof ValidEmail)) {
    yield put(setEmail(new Loaded(left(validEmail))));
  } else {
    yield put(setEmail(new Loading(left(ValidEmail.create(action.payload.email)))));

    const result: AsyncResult<any> = yield call(userApi.validateEmail, action.payload.email);

    const isEmailTaken = isLeft(result);

    const mapEmail = (email: string) =>
      isEmailTaken ? left(ValidEmail.create(email)) : right(ValidEmail.create(email) as ValidEmail);

    const emailValue = new Loaded(mapEmail(action.payload.email));

    yield put(setEmail(emailValue));
  }
}

export function* validateEmailSaga() {
  yield takeLatest(AgentAdministrationActionType.VALIDATE_EMAIL, validateEmailApi);
}

export function* impersonateAgentApi(action: ReturnType<typeof impersonateAgent>) {
  yield put(
    toggleRefreshSpinner(
      true,
      `Emulating ${action.payload.extraNameDetails[0]} ${action.payload.extraNameDetails[1]} (${action.payload.impersonatedUserName})`
    )
  );
  yield call(userApi.impersonate, action.payload.username, action.payload.impersonatedUserName);
  localStorage.setItem(TokenRefreshLocalKey.IN_EMULATION, 'true');
  setRefreshReRouteOptions(true);
  yield put(initiateTokenRefresh());
}

export function* impersonateAgentSaga() {
  yield takeLatest(AgentAdministrationActionType.IMPERSONATE_AGENT, impersonateAgentApi);
}

export function* stopImpersonationApi(action: ReturnType<typeof stopImpersonation>) {
  yield put(toggleRefreshSpinner(true, `Returning to Agent List...`));
  yield call(userApi.stopImpersonation, action.payload.username);
  localStorage.setItem(TokenRefreshLocalKey.IN_EMULATION, 'false');
  sessionStorage.removeItem(TokenRefreshSessionKeys.SAVED_ENCRYPTED_ID);
  setRefreshReRouteOptions(true, AgencyAdministrationRouteConstants.AGENTS);
  yield put(initiateTokenRefresh());
}

export function* clearEmulationApi(action: ReturnType<typeof clearEmulation>) {
  yield put(toggleRefreshSpinner(true, `Ending Emulation...`));
  yield call(userApi.stopImpersonation, action.payload.username);
  localStorage.setItem(TokenRefreshLocalKey.IN_EMULATION, 'false');
  sessionStorage.removeItem(TokenRefreshSessionKeys.SAVED_ENCRYPTED_ID);
  setRefreshReRouteOptions(true, "/");
  yield put(initiateTokenRefresh());
}

export function* stopImpersonationSaga() {
  yield takeEvery(AgentAdministrationActionType.STOP_IMPERSONATION, stopImpersonationApi);
}

export function* clearEmulationSaga() {
  yield takeEvery(AgentAdministrationActionType.CLEAR_EMULATION, clearEmulationApi);
}

export function* offboardAgentGenerator(action: ReturnType<typeof offboardAgent>) {
  try {
    action.payload.changeAgent(new Loading());
    const offboardedAgent = yield call(agentApi.offboardAgent, action.payload.agentId);
    action.payload.changeAgent(new Loaded(offboardedAgent));
  } catch {
    action.payload.changeAgent(new ServerError());
  }
}
export function* watchOffboardAgent() {
  yield takeEvery(AgentAdministrationActionType.OFFBOARD_AGENT, offboardAgentGenerator);
}
export const getAgents = (action: ReturnType<typeof fetchAgents>) =>
  PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<UserModel>(additionalPage),
    Initial: () => agentApi.listUsers(),
  })(action.payload);
export function* watchFetchAgents() {
  yield takeLatest(AgentAdministrationActionType.FETCH_AGENTS, getApiCallGenerator(getAgents, changeAgents));
}
export function* saveAgentMapping(action: ReturnType<typeof createAgentMapping>) {
  yield put(changeAgentMapping(new Loading()));
  const {isSupport} = action.payload;
  const selectedAgency: ReducerState['user']['profile']['selectedAgency'] = yield select(
    (state: ReducerState) => state.user.profile.selectedAgency
  );
  const encryptedIdentifier = pipe(selectedAgency, O.map(prop('encryptedIdentifier')), O.toUndefined);

  const agentResult: AsyncResult<unknown> = yield call(
    agentApi.createAgent,
    action.payload.agentMapping,
    isSupport,
    action.payload.id,
    encryptedIdentifier
  );

  if (isRight(agentResult)) {
    yield put(goBack());
  } else {
    const newMapping =
      agentResult.left instanceof ServerError
        ? fromResult(new ErrorSavingAgentMapping({...action.payload.agentMapping.value}, agentResult.left.message))
        : fromResult(new ErrorSavingAgentMapping({...action.payload.agentMapping.value}, agentResult.left.message));

    yield put(loadAgentMapping(newMapping));
  }
}
export function* watchCreateAgentMapping() {
  yield takeLatest(AgentAdministrationActionType.CREATE_AGENT_MAPPING, saveAgentMapping);
}
const getAgentMapping = (action: ReturnType<typeof getAgent>) => {
  return agentApi.getAgent(action.payload.agentId, action.payload.encryptedIdentifier).then(mappingValueEither =>
    pipe(
      mappingValueEither,
      mapEither(mappingPayload => ({value: toAgentMappingValue(mappingPayload)} as AgentMapping))
    )
  );
};
export function* watchGetAgentMapping() {
  yield takeLatest(AgentAdministrationActionType.GET_AGENT, getApiCallGenerator(getAgentMapping, loadAgentMapping));
}

export default function* agentSaga() {
  yield all([
    watchCreateAgentMapping(),
    watchGetAgentMapping(),
    watchFetchAgents(),
    watchOffboardAgent(),
    stopImpersonationSaga(),
    clearEmulationSaga(),
    impersonateAgentSaga(),
    validateEmailSaga(),
    validatePhoneNumberSaga(),
    validateUsernameSaga(),
  ]);
}
